import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "./i18n";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar, Footer } from "./components/Layout";
import Home from "./pages/Home";
import Login, { Register } from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import Reports from "./pages/Reports";
import Admin from "./pages/Admin";
import Embed from "./pages/Embed";
import YearDetail from "./pages/YearDetail";
import Methodology from "./pages/Methodology";
import Guardian from "./pages/Guardian";
import GuardianReports from "./pages/GuardianReports";
import GuardianYearDetail from "./pages/GuardianYearDetail";
import Downloads from "./pages/Downloads";
import EAStore from "./pages/EAStore";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.state?.user && !adminOnly) return children;
  if (loading || user === null) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono text-sm">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname.startsWith("/embed")) return <Embed />;
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/guardian" element={<Guardian />} />
          <Route path="/guardian/reports" element={<GuardianReports />} />
          <Route path="/guardian/year/:year" element={<GuardianYearDetail />} />
          <Route path="/performance/:year" element={<YearDetail />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/downloads" element={<Protected><Downloads /></Protected>} />
          <Route path="/expert-advisors" element={<EAStore />} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
